import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Bookmark, ChevronLeft, ChevronRight, Play, Check, Clock, Settings, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import Latex from '../../../../../apps/frontend/src/components/global/Latex';
import { QUESTION_DIFFICULTY } from '../../utils/Question_Difficulty';
import YoutubeVideoSolution from '../solution/VideoSolution';
import { getOption, getNumericalOption } from '../solution/Options';
import BookmarkController from '../../MyMistakes/Components/BookmarkController';
import { InfoIcon, PencilIcon } from '@phosphor-icons/react';
import axios from 'axios';
import { QUESTION_TYPE } from '../../utils/Question_Type';
import AttemptSimilarQsModal from '../../SimilarQuestion/AttemptSimilarQsModal';
import { toast } from 'react-hot-toast';
import TorchGuide from '../../components/generic/TorchGuide';
import SimilarQuestionSparkle from '../../../../../apps/frontend/public/icons/similar-question-sparkle.svg';
import VideoCipherPlayer from '../solution/VideoCipherPlayer';

const statusStyles = {
  wasted: { color: 'var(--q3-danger-normal)', borderColor: 'var(--q3-danger-light)' },
  confused: { color: 'var(--q3-neutral-default)', borderColor: 'var(--q3-stroke-normal)' },
  overtime: { color: 'var(--q3-yellow-normal)', borderColor: 'var(--q3-yellow-light)' },
  perfect: { color: 'var(--q3-success-normal)', borderColor: 'var(--q3-success-light)' },
  default: { color: 'var(--q3-neutral-default)', borderColor: 'var(--q3-neutral-default)' },
};

const getStatusStyles = (status) => statusStyles[status?.toLowerCase()] || statusStyles.default;
const getDifficultyStyle = (level) => {
  const styles = {
    3: { color: 'var(--q3-danger-normal)' },
    2: { color: 'var(--q3-yellow-normal)' },
    1: { color: 'var(--q3-success-normal)' },
  };
  return styles[level] || {};
};

export default function QuestionDrawer({
  isOpen,
  onClose,
  questions,
  currentQuestionIndex,
  onQuestionChange,
  testId,
  packId,
  isChapterWise = false,
}) {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [showSolution, setShowSolution] = useState(false);
  const [isAnswered, setIsAnswered] = useState(false);
  const [isRendered, setIsRendered] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [time, setTime] = useState(0);
  const [timerState, setTimerState] = useState('initial');
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('userProfile')));
  const [showTorchGuide, setShowTorchGuide] = useState(true);
  const [stepIndex, setStepIndex] = useState(0);
  const [autoStartTimer, setAutoStartTimer] = useState(user?.settings?.toggleQuestionTimer);
  const [inputValue, setInputValue] = useState('');
  const [showHideMistakeBar, setShowHideMistakeBar] = useState(true);

  const solutionRef = useRef(null);
  const similarQuestionButtonRef = useRef(null);
  const currentQuestion = questions[currentQuestionIndex];
  const tagDetail = currentQuestion?.user_question_tags?.tags_detail;

  // 🔁 Similar Questions States + Cache
  const [showSimilarQsModal, setShowSimilarQsModal] = useState(false);
  const [similarQsLoading, setSimilarQsLoading] = useState(false);
  const [similarQsData, setSimilarQsData] = useState(null);
  const [similarQsCache, setSimilarQsCache] = useState({}); // { [questionId]: data }
  const [showSimilarButton, setShowSimilarButton] = useState(false);
  const [similarQuestionsViewed, setSimilarQuestionsViewed] = useState(false);

  // Prevent body scroll
  useEffect(() => {
    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      return () => (document.body.style.overflow = originalStyle);
    }
  }, [isOpen]);

  // Reset state on open or question change
  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
      setSelectedOptions([]);
      setShowSolution(false);
      setIsAnswered(false);
      setTime(0);
      setInputValue('');
      setShowHideMistakeBar(true);
      setSimilarQuestionsViewed(false);
      setShowSimilarButton(false);

      if (autoStartTimer) setTimerState('running');
      else setTimerState('initial');

      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
      setTimerState('stopped');
    }
  }, [isOpen, currentQuestionIndex, autoStartTimer]);

  // Timer
  useEffect(() => {
    let interval = null;
    if (timerState === 'running') {
      interval = setInterval(() => setTime((prev) => prev + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timerState]);

  // Check if similar questions button should show
  const checkSimilarQuestionsButton = async (questionId) => {
    if (!questionId) return;
    try {
      const response = await axios.get(`/api/v1/similar-question/${questionId}/show-button`);
      if (response.status === 200) setShowSimilarButton(response.data.showSimilarQuestionsButton);
    } catch {
      setShowSimilarButton(false);
    }
  };

  const steps = [
    {
      target: '.similar-question-button-target',
      content: 'Click here to Practice Similar Questions',
      title: 'Attempt Similar Questions',
      disableBeacon: true,
      spotlightPadding: 0,
    },
  ];

  useEffect(() => {
    const checkIfTablet = () => {
      setShowTorchGuide(window.innerWidth > 1024);
    };

    checkIfTablet();
    window.addEventListener('resize', checkIfTablet);
    return () => window.removeEventListener('resize', checkIfTablet);
  }, []);

  // Add torch guide callback handler
  const handleJoyrideCallback = async (data) => {
    const { status, index, type, action } = data;

    if (status === 'finished') {
      setStepIndex(0);
      await axios.patch('/api/user/profile', {
        similarQuestionTourDone: true,
      });
      const updatedUser = {
        ...user,
        similarQuestionTourDone: true,
      };
      localStorage.setItem('userProfile', JSON.stringify(updatedUser));
      setUser(updatedUser);
    } else if (type === 'step:after') {
      if (action === 'next') {
        setStepIndex(index + 1);
      } else if (action === 'prev') {
        setStepIndex(Math.max(0, index - 1));
      }
    }
  };

  // Track that user has viewed similar questions
  const trackSimilarQuestionsViewed = async () => {
    try {
      await axios.post(`/api/v1/similar-question/${currentQuestion?.question?._id}/track`);
      setSimilarQuestionsViewed(true);
    } catch {}
  };

  // 🧠 Fetch similar questions + Cache
  const getSimilarQuestions = async () => {
    try {
      const response = await axios.get(`/api/v1/similar-question/${currentQuestion?.question?._id}/similar-questions`);
      if (response.status === 200) {
        const data = {
          questions: response.data.similarQuestions,
          packId,
          testId,
        };
        setSimilarQsData(data);
        setSimilarQsCache((prev) => ({
          ...prev,
          [currentQuestion?.question?._id]: data,
        }));
      }
    } catch {
      toast.error('Failed to fetch similar questions');
    }
  };

  const handleAttemptSimilarQs = async () => {
    setShowSimilarQsModal(true);

    const qid = currentQuestion?.question?._id;
    if (!qid) return;

    // Use cache instantly if available
    if (similarQsCache[qid]) {
      setSimilarQsData(similarQsCache[qid]);
      return;
    }

    // Otherwise, fetch and show loader
    setSimilarQsLoading(true);
    await getSimilarQuestions();
    setSimilarQsLoading(false);
  };

  useEffect(() => {
    if (isOpen && currentQuestion?.question?._id) {
      checkSimilarQuestionsButton(currentQuestion.question._id);
    }

    // 🧠 Auto-load from cache when switching questions
    const qid = currentQuestion?.question?._id;
    if (similarQsCache[qid]) {
      setSimilarQsData(similarQsCache[qid]);
    } else {
      setSimilarQsData(null);
    }
  }, [isOpen, currentQuestion?.question?._id, showSimilarQsModal]);

  const handleOptionSelect = (optionId) => {
    if (currentQuestion.type === 'multipleCorrect') {
      setSelectedOptions((prev) =>
        prev.includes(optionId) ? prev.filter((id) => id !== optionId) : [...prev, optionId],
      );
    } else {
      setSelectedOptions([optionId]);
    }
  };

  const handleAnimationEnd = () => {
    if (!isOpen) {
      setIsRendered(false);
    }
  };

  const handleStartTimer = () => {
    setTimerState('running');
  };

  const handleAutoStartToggle = async (newSetting) => {
    setAutoStartTimer(newSetting);
    await axios.patch('/api/user/profile/settings', { toggleQuestionTimer: newSetting });
    if (newSetting && timerState === 'initial') {
      handleStartTimer();
    }
  };

  const handleViewSolution = () => {
    setShowSolution(true);
    setTimeout(() => {
      solutionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const hideAnswer = async () => {
    try {
      await axios.patch(`/api/v1/answers/${currentQuestion._id}/hide`);
      setShowHideMistakeBar(false);
    } catch (error) {}
  };

  const handleCheckAnswer = () => {
    if (selectedOptions?.length || currentQuestion.type === 'numerical') {
      setIsAnswered(true);
      setTimerState('stopped');
    }
  };

  // FIXED: Reset states immediately before navigation
  const handleNext = () => {
    setSelectedOptions([]);
    setShowSolution(false);
    setIsAnswered(false);
    setTime(0);
    setInputValue('');
    setShowHideMistakeBar(true);
    onQuestionChange(currentQuestionIndex + 1);
  };

  // FIXED: Reset states immediately before navigation
  const handlePrevious = () => {
    setSelectedOptions([]);
    setShowSolution(false);
    setIsAnswered(false);
    setTime(0);
    setInputValue('');
    setShowHideMistakeBar(true);
    onQuestionChange(currentQuestionIndex - 1);
  };

  const formatTimeTaken = (seconds) => {
    if (typeof seconds !== 'number' || seconds < 0) return '0sec';

    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    if (mins > 0) {
      return `${mins}mins:${secs}sec`;
    }
    return `${secs}sec`;
  };

  const formatLiveTimer = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
  };

  const currentSolution = useMemo(() => {
    const userInputValue = inputValue || currentQuestion?.inputValue;
    const correctValue = currentQuestion?.question?.correctValue;

    return {
      status: isAnswered ? 'answered' : 'not-answered',
      markedOptions: selectedOptions.map((id) => ({ optionId: id })), // multiple options
      inputValue: isAnswered
        ? {
            value: userInputValue,
            isCorrect:
              userInputValue && correctValue && Math.abs(parseFloat(userInputValue) - parseFloat(correctValue)) <= 0.01,
          }
        : userInputValue,
      questionId: currentQuestion?.question,
    };
  }, [isAnswered, selectedOptions, currentQuestion, inputValue]);

  const optionsBefore = useMemo(() => {
    const options = currentQuestion?.question?.options;
    if (!Array.isArray(options)) return null;

    return options.map((option, index) => {
      const isSelected = selectedOptions.includes(option.id);
      return (
        <div key={option.id} className="h-full cursor-pointer" onClick={() => handleOptionSelect(option.id)}>
          <div className="w-full mb-3">
            <div className="relative">
              <div
                className={`h-full mb-0 rounded-[12px] border ${
                  isSelected
                    ? 'border-[var(--q3-accent-normal)] bg-blue-50/50'
                    : 'bg-[var(--q3-surface-dim)] border-transparent'
                }`}
              >
                <div className="px-4 py-3 flex items-center">
                  <div className="flex items-center">
                    <div className="mr-4">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)]">
                        {String.fromCharCode('A'.charCodeAt(0) + index)}
                      </div>
                    </div>
                    <div className="flex">
                      {option.imageUrl ? (
                        <img src={option.imageUrl} alt={`Option ${index + 1}`} />
                      ) : (
                        <Latex>{option.text}</Latex>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    });
  }, [currentQuestion, selectedOptions]);

  // FIXED: Added isAnswered dependency and null check to prevent flash
  const optionsAfter = useMemo(() => {
    const options = currentQuestion?.question?.options;
    if (!Array.isArray(options) || !isAnswered) return null; // FIXED: Return null when not answered

    return options.map((option, index) => (
      <div key={option.id} className="h-full">
        {getOption(currentSolution, option, index)}
      </div>
    ));
  }, [currentQuestion, currentSolution, isAnswered]); // FIXED: Added isAnswered dependency

  if (!isRendered || !currentQuestion) return null;

  const statusStyle = getStatusStyles(currentQuestion.attemptOverview);
  const difficultyStyle = getDifficultyStyle(currentQuestion.question.level);

  return (
    <div className="fixed inset-0 z-50 flex justify-end backdrop-blur-lg">
      {/* Torch Guide */}
      {!user?.similarQuestionTourDone && showTorchGuide && (
        <TorchGuide steps={steps} handleTorchGuideCallback={handleJoyrideCallback} stepIndex={stepIndex} />
      )}
      {/* Backdrop overlay that prevents scrolling */}
      <div
        className={`absolute inset-0 bg-black/30 transition-opacity duration-700 ease-in-out ${isAnimating ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
        style={{ touchAction: 'none' }} // Prevents touch scrolling on mobile
      />

      <div
        className={`relative flex flex-col h-full bg-[var(--q3-surface-default)] shadow-xl w-full lg:w-[60vw] transition-transform duration-700 ease-in-out transform ${isAnimating ? 'translate-x-0' : 'translate-x-full'}`}
        onTransitionEnd={handleAnimationEnd}
      >
        <Button
          onClick={onClose}
          size="icon"
          className="hidden lg:inline-flex rounded-full absolute top-5 -left-10 w-8 h-8 bg-[var(--q3-surface-default)] hover:bg-[var(--q3-surface-dim)]"
        >
          <X size={24} className="text-[var(--q3-neutral-default)]" />
        </Button>
        <div className="border-b border-[var(--q3-stroke-normal)] p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-[var(--q3-neutral-default)]">
              Qs {currentQuestionIndex + 1} • {QUESTION_TYPE[currentQuestion.type]}
            </span>
          </div>

          <div className="flex items-center justify-center w-40 h-10">
            {' '}
            {/* This wrapper prevents layout shift */}
            {timerState === 'initial' && (
              <Button
                variant="outline"
                className="rounded-full border-[var(--q3-accent-normal)] text-[var(--q3-accent-normal)] hover:text-[var(--q3-accent-normal)] hover:bg-blue-50/50 px-4 py-1 h-auto"
                onClick={handleStartTimer}
              >
                <Clock size={16} className="mr-2" />
                Start Timer
              </Button>
            )}
            {(timerState === 'running' || timerState === 'stopped') && (
              <div className="rounded-full border border-dashed border-gray-300 px-3 py-1 flex items-center gap-2">
                <Clock size={16} className="text-[var(--q3-accent-normal)]" />
                <span className="text-base font-medium text-[var(--q3-accent-normal)] tabular-nums w-20">
                  {formatLiveTimer(time)}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <BookmarkController
              questionId={currentQuestion.question._id}
              packId={packId}
              testId={testId}
              isNoteButton={true}
            />
            <BookmarkController questionId={currentQuestion.question._id} packId={packId} testId={testId} />

            {/* Updated Section: Using the imported Dialog component */}
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  size="icon"
                  className="rounded-full w-8 h-8 bg-transparent hover:bg-[var(--q3-surface-dim)] items-center justify-center border border-[var(--q3-stroke-normal)]"
                >
                  <Settings size={18} className="text-[var(--q3-neutral-default)]" />
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px] bg-[var(--q3-surface-default)] border-[var(--q3-stroke-normal)] px-0 backdrop-blur-sm">
                {/* <DialogOverlay /> */}
                <DialogHeader className="px-4">
                  <DialogTitle className="text-[var(--q3-neutral-default)]">Practice Mode Settings</DialogTitle>
                </DialogHeader>
                <hr />
                <div className="py-4 px-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm text-[var(--q3-neutral-default)]">Auto-start Timer</p>
                      <p className="text-sm text-[var(--q3-neutral-light)]">
                        Start timer automatically when opening a question
                      </p>
                    </div>
                    <button
                      onClick={() => handleAutoStartToggle(!autoStartTimer)}
                      className={`relative cursor-pointer inline-flex items-center h-6 rounded-full w-11 transition-colors ${autoStartTimer ? 'bg-[var(--q3-accent-normal)]' : 'bg-[var(--q3-surface-dimmest)]'}`}
                    >
                      <span
                        className={`inline-block w-4 h-4 transform bg-[var(--q3-neutral-default_inverted)] rounded-full transition-transform ${autoStartTimer ? 'translate-x-6' : 'translate-x-1'}`}
                      />
                    </button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Button
              onClick={onClose}
              size="icon"
              className="inline-flex lg:hidden rounded-full w-8 h-8 bg-transparent hover:bg-[var(--q3-surface-dim)] items-center justify-center"
            >
              <X size={24} className="text-[var(--q3-neutral-default)]" />
            </Button>
          </div>
        </div>

        <div className="flex-grow p-6 overflow-y-auto">
          <div className="flex items-center flex-wrap gap-4 mb-4">
            {tagDetail?.title && tagDetail?.title?.toLowerCase() !== 'not tagged' && (
              <Badge variant="outline" className="text-xs uppercase font-semibold flex items-center gap-1.5">
                {tagDetail.icon && <img src={tagDetail.icon} alt={tagDetail.title} className="w-4 h-4" />}
                {tagDetail.title}
              </Badge>
            )}
            {tagDetail?.title && <div className="h-5 w-[1px] bg-[var(--q3-stroke-normal)]" />}
            <Badge variant="outline" className="text-xs uppercase font-semibold" style={difficultyStyle}>
              {QUESTION_DIFFICULTY[currentQuestion.question.level]}
            </Badge>
            {currentQuestion.attemptOverview && <div className="h-5 w-[1px] bg-[var(--q3-stroke-normal)]" />}
            {currentQuestion.attemptOverview && (
              <div>
                <Badge
                  variant="outline"
                  className="text-xs uppercase font-semibold"
                  style={{ color: statusStyle.color, borderColor: statusStyle.borderColor }}
                >
                  {currentQuestion?.attemptOverview}
                </Badge>
              </div>
            )}

            {currentQuestion.attemptOverview && <div className="h-5 w-[1px] bg-[var(--q3-stroke-normal)]" />}
          </div>
          <div className="my-6 image-invert-wrapper">
            {currentQuestion?.question?.question?.text ? (
              <Latex className="text-[var(--q3-neutral-default)] text-base leading-relaxed">
                {currentQuestion.question.question.text}
              </Latex>
            ) : (
              <p className="text-sm text-[var(--q3-neutral-light)] italic">Question text is not available.</p>
            )}
          </div>

          <div className="mb-6">
            {currentQuestion.type === 'numerical' ? (
              <div className="w-full">
                {!isAnswered ? (
                  <NumericalInput value={inputValue} onChange={setInputValue} />
                ) : (
                  getNumericalOption(currentSolution)
                )}
              </div>
            ) : (
              // FIXED: Simplified conditional rendering to prevent flash
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 image-invert-wrapper">
                {isAnswered ? optionsAfter : optionsBefore}
              </div>
            )}
          </div>

          <div ref={solutionRef}>
            {showSolution && (
              <div className="mb-6 space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-[var(--q3-neutral-light)] mb-3">TEXT SOLUTION</h3>
                  <div className="bg-[var(--q3-surface-dim)] p-4 rounded-lg image-invert-wrapper">
                    {currentQuestion.question?.solution?.text ? (
                      <Latex className="text-sm text-[var(--q3-neutral-default)] leading-relaxed">
                        {currentQuestion.question.solution.text}
                      </Latex>
                    ) : (
                      <p className="text-sm text-[var(--q3-neutral-light)] italic">No text solution available.</p>
                    )}
                  </div>
                </div>
                {currentQuestion.question?.videoSolution ? (
                  <VideoCipherPlayer
                    videoId={currentQuestion.question.videoSolution}
                    isSolution={true}
                    testId={testId}
                    questionId={currentQuestion.question._id}
                  />
                ) : null}
              </div>
            )}
          </div>
        </div>

        {/* Attempt Similar Qs Button ABOVE Mistake Bar */}
        {showSimilarButton && showHideMistakeBar && !isChapterWise && (
          <div className="p-4 flex flex-row items-center justify-center">
            <div
              className="p-[1px] rounded-[60px] w-fit"
              style={{
                background:
                  'linear-gradient(to right, var(--q3-stroke-light) 0%, var(--q3-stroke-light) 60%, var(--q3-violet-normal) 80%, var(--q3-rose-normal) 100%)',
              }}
            >
              <button
                ref={similarQuestionButtonRef}
                className="similar-question-button-target flex items-center gap-2 px-5 py-2 bg-[var(--q3-surface-default)] rounded-[60px] shadow-md cursor-pointer"
                onClick={handleAttemptSimilarQs}
                style={{
                  boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                }}
              >
                <img src={SimilarQuestionSparkle} alt="Similar Question Sparkle" width={22} height={22} />
                <span
                  style={{
                    background: 'linear-gradient(to right, var(--q3-violet-normal), var(--q3-rose-normal))',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  Attempt Similar Qs
                </span>
              </button>
            </div>
          </div>
        )}

        {showHideMistakeBar && (
          <div className="p-4 bg-[var(--q3-surface-dim)] text-[var(--q3-neutral-default)] flex flex-row items-center justify-between">
            <div className="flex flex-row items-center gap-2">
              <InfoIcon size={24} />
              Do you want to remove this question from mistakes?
            </div>
            <div className="flex flex-row items-center gap-2">
              <Button
                variant="outline"
                className="!bg-transparent border-[var(--q3-accent-normal)] text-[var(--q3-accent-normal)] rounded-full"
                onClick={hideAnswer}
              >
                Yes
              </Button>
              <Button
                variant="outline"
                className="!bg-transparent border-[var(--q3-danger-normal)] text-[var(--q3-danger-normal)] rounded-full"
                onClick={() => setShowHideMistakeBar(false)}
              >
                No
              </Button>
            </div>
          </div>
        )}

        {/* Attempt Similar Qs Button BELOW once bar is removed */}
        {showSimilarButton && !showHideMistakeBar && !isChapterWise && (
          <div className="p-4 flex flex-row items-center justify-center">
            <div
              className="p-[1px] rounded-[60px] w-fit"
              style={{
                background:
                  'linear-gradient(to right, var(--q3-stroke-light) 0%, var(--q3-stroke-light) 60%, var(--q3-violet-normal) 80%, var(--q3-rose-normal) 100%)',
              }}
            >
              <button
                onClick={handleAttemptSimilarQs}
                className="flex items-center gap-2 px-5 py-2 bg-[var(--q3-surface-default)] rounded-[60px] shadow-md cursor-pointer"
                style={{
                  boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <path
                    d="M16.9795 11.0911L12.5469 9.45313L10.9141 5.01703C10.8174 4.75435 10.6424 4.52764 10.4129 4.3675C10.1833 4.20737 9.91009 4.12151 9.63017 4.12151C9.35025 4.12151 9.07707 4.20737 8.84749 4.3675C8.6179 4.52764 8.44296 4.75435 8.34627 5.01703L6.70314 9.45313L2.26705 11.0859C2.00436 11.1826 1.77765 11.3576 1.61752 11.5872C1.45738 11.8167 1.37152 12.0899 1.37152 12.3698C1.37152 12.6498 1.45738 12.9229 1.61752 13.1525C1.77765 13.3821 2.00436 13.5571 2.26705 13.6538L6.70314 15.2969L8.33595 19.733C8.43265 19.9957 8.60759 20.2224 8.83717 20.3825C9.06676 20.5426 9.33994 20.6285 9.61986 20.6285C9.89978 20.6285 10.173 20.5426 10.4025 20.3825C10.6321 20.2224 10.8071 19.9957 10.9038 19.733L12.5469 15.2969L16.983 13.6641C17.2457 13.5674 17.4724 13.3924 17.6325 13.1628C17.7926 12.9333 17.8785 12.6601 17.8785 12.3802C17.8785 12.1002 17.7926 11.8271 17.6325 11.5975C17.4724 11.3679 17.2457 11.1929 16.983 11.0963L16.9795 11.0911ZM11.7735 14.1127C11.6801 14.147 11.5954 14.2013 11.5251 14.2716C11.4547 14.3419 11.4005 14.4267 11.3661 14.52L9.62502 19.2371L7.88736 14.5234C7.85302 14.4291 7.79845 14.3435 7.72749 14.2725C7.65652 14.2016 7.57088 14.147 7.47658 14.1127L2.76291 12.375L7.47658 10.6373C7.57088 10.603 7.65652 10.5484 7.72749 10.4775C7.79845 10.4065 7.85302 10.3209 7.88736 10.2266L9.62502 5.51289L11.3627 10.2266C11.3971 10.3199 11.4513 10.4046 11.5216 10.475C11.5919 10.5453 11.6767 10.5995 11.77 10.6339L16.4871 12.375L11.7735 14.1127ZM12.375 3.4375C12.375 3.25516 12.4474 3.0803 12.5764 2.95136C12.7053 2.82243 12.8802 2.75 13.0625 2.75H14.4375V1.375C14.4375 1.19266 14.5099 1.0178 14.6389 0.888864C14.7678 0.759933 14.9427 0.6875 15.125 0.6875C15.3074 0.6875 15.4822 0.759933 15.6112 0.888864C15.7401 1.0178 15.8125 1.19266 15.8125 1.375V2.75H17.1875C17.3699 2.75 17.5447 2.82243 17.6737 2.95136C17.8026 3.0803 17.875 3.25516 17.875 3.4375C17.875 3.61984 17.8026 3.7947 17.6737 3.92364C17.5447 4.05257 17.3699 4.125 17.1875 4.125H15.8125V5.5C15.8125 5.68234 15.7401 5.8572 15.6112 5.98614C15.4822 6.11507 15.3074 6.1875 15.125 6.1875C14.9427 6.1875 14.7678 6.11507 14.6389 5.98614C14.5099 5.8572 14.4375 5.68234 14.4375 5.5V4.125H13.0625C12.8802 4.125 12.7053 4.05257 12.5764 3.92364C12.4474 3.7947 12.375 3.61984 12.375 3.4375ZM21.3125 7.5625C21.3125 7.74484 21.2401 7.9197 21.1112 8.04864C20.9822 8.17757 20.8074 8.25 20.625 8.25H19.9375V8.9375C19.9375 9.11984 19.8651 9.29471 19.7362 9.42364C19.6072 9.55257 19.4324 9.625 19.25 9.625C19.0677 9.625 18.8928 9.55257 18.7639 9.42364C18.6349 9.29471 18.5625 9.11984 18.5625 8.9375V8.25H17.875C17.6927 8.25 17.5178 8.17757 17.3889 8.04864C17.2599 7.9197 17.1875 7.74484 17.1875 7.5625C17.1875 7.38016 17.2599 7.2053 17.3889 7.07636C17.5178 6.94743 17.6927 6.875 17.875 6.875H18.5625V6.1875C18.5625 6.00516 18.6349 5.8303 18.7639 5.70136C18.8928 5.57243 19.0677 5.5 19.25 5.5C19.4324 5.5 19.6072 5.57243 19.7362 5.70136C19.8651 5.8303 19.9375 6.00516 19.9375 6.1875V6.875H20.625C20.8074 6.875 20.9822 6.94743 21.1112 7.07636C21.2401 7.2053 21.3125 7.38016 21.3125 7.5625Z"
                    fill="url(#paint0_linear_13883_3155)"
                  />
                  <defs>
                    <linearGradient
                      id="paint0_linear_13883_3155"
                      x1="1.37152"
                      y1="10.658"
                      x2="21.3125"
                      y2="10.658"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#EC003F" />
                      <stop offset="1" stopColor="#7F22FE" />
                    </linearGradient>
                  </defs>
                </svg>

                <span
                  style={{
                    background: 'linear-gradient(to right, var(--q3-violet-normal), var(--q3-rose-normal))',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  Attempt Similar Qs
                </span>
              </button>
            </div>
          </div>
        )}

        <div className="border-t border-[var(--q3-stroke-normal)] p-4 bg-[var(--q3-surface-default)]">
          <div className="flex items-center w-full gap-3">
            <Button
              onClick={handlePrevious}
              variant="outline"
              disabled={currentQuestionIndex === 0}
              className="flex-1 rounded-full bg-transparent"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>

            {!isAnswered && (
              <Button
                onClick={handleCheckAnswer}
                disabled={!selectedOptions?.length && !inputValue}
                className="bg-[var(--q3-accent-normal)] rounded-full px-8 flex-1"
              >
                Check Answer
              </Button>
            )}

            {isAnswered && !showSolution && (
              <Button onClick={handleViewSolution} className="bg-[var(--q3-accent-normal)] rounded-full px-8 flex-1">
                View solution
              </Button>
            )}

            {/* Add flex-1 to the placeholder div to maintain spacing */}
            {showSolution && <div className="flex-1" />}

            <Button
              onClick={handleNext}
              variant="outline"
              disabled={currentQuestionIndex === questions.length - 1}
              className="flex-1 rounded-full bg-transparent"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      {showSimilarQsModal && (
        <AttemptSimilarQsModal
          loading={similarQsLoading}
          data={similarQsData || similarQsCache[currentQuestion?.question?._id]}
          onClose={() => setShowSimilarQsModal(false)}
          packId={packId}
          testId={testId}
        />
      )}
    </div>
  );
}

const NumericalInput = ({ value, onChange }) => {
  return (
    <div className="w-full flex flex-col md:flex-row gap-4 mb-4">
      <div className="flex-1 bg-[var(--q3-surface-dim)] rounded-lg">
        <div className="px-4 py-3 flex flex-col justify-center h-full">
          <div className="flex items-center justify-between px-2">
            <div className="flex flex-col gap-y-1 text-base font-medium text-[var(--q3-neutral-default)] w-full">
              <input
                type="number"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Enter your answer"
                className="w-full rounded-lg border bg-[var(--q3-surface-default)] px-3 py-2 text-base text-[var(--q3-neutral-default)] focus:outline-none focus:ring-2 focus:ring-[var(--q3-accent-normal)]"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 bg-[var(--q3-surface-default)]"></div>
    </div>
  );
};
